/* eslint-disable react/jsx-pascal-case */

import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  Box,
  Card,
  Flex,
  Heading,
  Hide,
  Link,
  Text,
  mediaQueries
} from 'pcln-design-system'
import { ImageTile } from '@pcln/content-tile'
import { PriceBox, StrikePrice } from '@pcln/price-box'
import ArrowButton from './ArrowButton'
import analytics from '../../utils/analytics'

const RemainingWidthFlex = styled(Flex)`
  flex-grow: 1;
`

const NoWrapText = styled(Text)`
  ${mediaQueries[1]} {
    white-space: nowrap;
  }
`

const NoDecorationLink = styled(Link)`
  text-decoration-line: none;
`

const TextSpan = Text.span

const getImageTile = (
  data,
  isUsingNavArrows,
  handleNavOnClick,
  isUsingPricePrefix,
  isUsingPriceType,
  isOpaque
) => (
  <Box>
    <ImageTile
      height={data.imageHeight}
      image={data.image}
      imageWebP={data.imageWebP}
      navArray={data.navArray}
      handleNavOnClick={isUsingNavArrows && handleNavOnClick}
      isOpaque={isOpaque}
    />
    <Text color="text" p={2}>
      <Flex justifyContent="space-between" alignItems="baseline">
        <Text fontSize={0}>{data.productName}</Text>
        {!!data.strikePrice && (
          <StrikePrice price={data.strikePrice} size="small" />
        )}
      </Flex>
      <Flex justifyContent="space-between" alignItems="baseline">
        <Box>
          <NoWrapText fontSize={3} fontWeight="bold">
            {data.cityName}
          </NoWrapText>
          <Text fontSize={0} mt={1}>
            {data.description}
          </Text>
        </Box>
        {!!data.priceAmount && (
          <NoWrapText mt="1px" ml={2}>
            <Flex alignItems="baseline">
              {isUsingPricePrefix && (
                <TextSpan
                  mx={2}
                  color="pricePrimary"
                  fontSize={4}
                  fontWeight="bold"
                >
                  {data.pricePrefix}
                </TextSpan>
              )}
              <PriceBox
                priceSize="small"
                price={data.priceAmount}
                priceColor="pricePrimary"
                priceType={isUsingPriceType && 'per person'}
                alignItems="right"
                toFixDecimal={0}
              />
            </Flex>
          </NoWrapText>
        )}
      </Flex>
    </Text>
  </Box>
)

// TODO: improve the list of params using "deeper" React pattern
const parseDealTiles = (
  dataArray,
  isUsingNavArrows,
  handleNavOnClick,
  isUsingPriceType,
  isUsingPricePrefix,
  isOpaque,
  position,
  numDealsToDisplay,
  isTrips
) => {
  const items = dataArray.slice(position, position + numDealsToDisplay)
  return items.map((data, index) => (
    <Card
      onClick={() => {
        analytics.fireEvent({
          action: data.gaAction,
          category: data.gaCategory,
          label: data.cityName
        })
      }}
      mx={[0, 2]}
      mb={index === dataArray.length - 1 ? 0 : [3, 0]}
      width={[1, 1 / numDealsToDisplay]}
      key={`deal-tile-${data.cityName}`}
      data-testid={`deal-tile-${data.cityName}`}
    >
      {isTrips ? (
        <Box onClick={data.onTileClick}>
          {getImageTile(
            data,
            isUsingNavArrows,
            handleNavOnClick,
            isUsingPricePrefix,
            isUsingPriceType,
            isOpaque,
            isTrips
          )}
        </Box>
      ) : (
        <NoDecorationLink href={data.navArray[0].href} target="_blank">
          {getImageTile(
            data,
            isUsingNavArrows,
            handleNavOnClick,
            isUsingPricePrefix,
            isUsingPriceType,
            isOpaque,
            false
          )}
        </NoDecorationLink>
      )}
    </Card>
  ))
}

class ScrollableDealModule extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      position: 0,
      numDealsToDisplay: 0
    }

    this.handleLeftArrowClick = this.updatePosition.bind(this, false)
    this.handleRightArrowClick = this.updatePosition.bind(this, true)
    this.handleResize = this.handleResize.bind(this)
  }

  componentDidMount() {
    this.setState({
      numDealsToDisplay:
        // eslint-disable-next-line no-nested-ternary
        window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1
    })
    window.addEventListener('resize', this.handleResize)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize() {
    const screenSize = window.innerWidth
    if (screenSize >= 1024 && this.state.numDealsToDisplay !== 3) {
      this.setState({ numDealsToDisplay: 3 })
    } else if (
      screenSize >= 640 &&
      screenSize < 1024 &&
      this.state.numDealsToDisplay !== 2
    ) {
      this.setState({ numDealsToDisplay: 2 })
    } else if (screenSize < 640 && this.state.numDealsToDisplay !== 1) {
      this.setState({ numDealsToDisplay: 1 })
    }
  }

  updatePosition(shouldAdd) {
    if (shouldAdd) {
      const maxIndex =
        this.props.content.dataArray.length - this.state.numDealsToDisplay
      const nextPosition = this.state.position + this.state.numDealsToDisplay
      const position = Math.min(nextPosition, maxIndex)
      this.setState({ position })
    } else {
      const nextPosition = this.state.position - this.state.numDealsToDisplay
      const position = Math.max(nextPosition, 0)
      this.setState({ position })
    }
  }

  render() {
    const {
      content,
      isUsingNavArrows,
      handleNavOnClick,
      isUsingPriceType,
      isUsingPricePrefix,
      isOpaque,
      isTrips
    } = this.props

    return (
      <Box>
        <Heading p={2} fontSize={4} fontWeight="bold" m={0}>
          <Hide xs>{content.heading}</Hide>
          <Hide sm md lg xl xxl>
            {content.headingMobile}
          </Hide>
        </Heading>
        <Flex justifyContent="center" alignItems="center">
          {isUsingNavArrows && (
            <Hide xs>
              <ArrowButton
                color="primary"
                direction="left"
                mr={3}
                disabled={this.state.position === 0}
                onClick={this.handleLeftArrowClick}
              />
            </Hide>
          )}
          <RemainingWidthFlex flexDirection={['column', 'row']} mx={2}>
            {parseDealTiles(
              content.dataArray,
              isUsingNavArrows,
              handleNavOnClick,
              isUsingPriceType,
              isUsingPricePrefix,
              isOpaque,
              this.state.position,
              this.state.numDealsToDisplay,
              isTrips
            )}
          </RemainingWidthFlex>
          {isUsingNavArrows && (
            <Hide xs>
              <ArrowButton
                color="primary"
                direction="right"
                disabled={
                  this.state.position >=
                  content.dataArray.length - this.state.numDealsToDisplay
                }
                onClick={this.handleRightArrowClick}
              />
            </Hide>
          )}
        </Flex>
      </Box>
    )
  }
}

ScrollableDealModule.displayName = 'ScrollableDealModule'

ScrollableDealModule.defaultProps = {
  isUsingNavArrows: true,
  isUsingPriceType: true,
  isUsingPricePrefix: false,
  isOpaque: true,
  isTrips: false
}

ScrollableDealModule.propTypes = {
  content: PropTypes.object.isRequired,
  isUsingNavArrows: PropTypes.bool,
  handleNavOnClick: PropTypes.func,
  isUsingPriceType: PropTypes.bool,
  isUsingPricePrefix: PropTypes.bool,
  isOpaque: PropTypes.bool,
  isTrips: PropTypes.bool
}

export default ScrollableDealModule
